
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, AlertTriangle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { usePlanRestrictions } from "@/hooks/usePlanRestrictions";
import { Badge } from "@/components/ui/badge";

interface Email {
  id: string;
  subject: string;
  sender: string;
  preview: string;
  date: string;
  content?: string;
  htmlContent?: string;
}

interface BulkResponseGeneratorProps {
  selectedEmails: Email[];
  onResponseGenerated: (emailId: string, response: string) => void;
  isGeneratingMultiple: boolean;
  setIsGeneratingMultiple: (value: boolean) => void;
  selectedAssistant: string;
}

export const BulkResponseGenerator = ({ 
  selectedEmails, 
  onResponseGenerated,
  isGeneratingMultiple,
  setIsGeneratingMultiple,
  selectedAssistant
}: BulkResponseGeneratorProps) => {
  const { toast } = useToast();
  const [progress, setProgress] = useState(0);
  const [generatedEmailIds, setGeneratedEmailIds] = useState<Set<string>>(new Set());
  const { planLimits, canProcessBulkEmails } = usePlanRestrictions();
  const lastSelectedCount = useRef(selectedEmails.length);

  // Only log when the count actually changes
  useEffect(() => {
    if (lastSelectedCount.current !== selectedEmails.length) {
      lastSelectedCount.current = selectedEmails.length;
    }
  }, [selectedEmails.length]);

  const handleGenerateAllResponses = async () => {
    if (!selectedAssistant) {
      toast({
        title: "Assistant Required",
        description: "Please select an AI assistant first.",
        variant: "destructive",
      });
      return;
    }

    if (!canProcessBulkEmails(selectedEmails.length)) {
      return;
    }

    setIsGeneratingMultiple(true);
    setProgress(0);

    try {
      const generateButtons = document.querySelectorAll('button[class*="w-full"]:not([disabled])') as NodeListOf<HTMLButtonElement>;
      
      const buttonsArray = Array.from(generateButtons)
        .filter(button => button.textContent?.includes('Generate Response'))
        .slice(0, selectedEmails.length);
      
      if (buttonsArray.length === 0) {
        throw new Error("No generate buttons found");
      }

      for (let i = 0; i < buttonsArray.length; i++) {
        const currentEmail = selectedEmails[i];
        buttonsArray[i].click();
        setGeneratedEmailIds(prev => new Set([...prev, currentEmail.id]));
        await new Promise(resolve => setTimeout(resolve, 1000));
        setProgress(((i + 1) / selectedEmails.length) * 100);
      }

      toast({
        title: "Bulk Generation Complete",
        description: `Generated responses for ${selectedEmails.length} emails.`,
      });
    } catch (error) {
      console.error("Error generating multiple responses:", error);
      toast({
        title: "Error",
        description: "Failed to generate responses. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingMultiple(false);
      setProgress(0);
    }
  };

  if (selectedEmails.length <= 1) return null;

  const isOverLimit = planLimits?.bulkEmailsLimit && selectedEmails.length > planLimits.bulkEmailsLimit;

  return (
    <div className="sticky top-4 z-10 bg-background p-4 shadow rounded-lg border">
      {isOverLimit && (
        <div className="mb-4 flex items-center gap-2 text-yellow-600 text-sm">
          <AlertTriangle className="h-4 w-4" />
          <span>
            Your plan allows processing up to {planLimits.bulkEmailsLimit} emails at once.
            <Button 
              variant="link" 
              className="px-1 h-auto text-yellow-600 font-semibold"
              onClick={() => window.location.href = "/settings?tab=tokens"}
            >
              Upgrade your plan
            </Button>
          </span>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button 
          className="w-full"
          onClick={handleGenerateAllResponses}
          disabled={isGeneratingMultiple || isOverLimit}
        >
          <Sparkles className="h-4 w-4 mr-2" />
          {isGeneratingMultiple 
            ? `Generating ${selectedEmails.length} Responses (${Math.round(progress)}%)` 
            : `Generate ${selectedEmails.length} Responses`}
        </Button>
        <Badge variant="secondary">
          {selectedEmails.length} selected
        </Badge>
      </div>
    </div>
  );
};
